import { useQuery } from '@tanstack/react-query';
import { FF_FIGMA_ORG_SCOPED_PUBLISHING } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useLinkedFigmaAccount } from '@/hooks/useLinkedFigmaAccount';
import useWorkspacesStore from '@/state/workspaces.state';
import { isEnterpriseWeaveSubscription } from '@/utils/communityPublishingEligibility.utils';
import {
  fetchEligibleFigmaOrgs,
  NotConnectedToFigmaError,
  type EligibleFigmaOrg,
} from '@/api/workflowCommunityPublishing.api';

export interface OrgPublishDestination {
  planId: string;
  name: string;
}

/**
 * - `loading`: the publish row is greyed out until we know your orgs.
 * - `unlinked`: the publish row asks you to link Figma first, since your orgs come from Figma.
 * - `error`: the publish row is blocked with "Couldn't load your Figma organizations".
 * - `ready`: the orgs you can publish to; empty means Community only (also when the flag or the
 *   workspace's org publishing setting is off).
 */
export type OrgLookupStatus = 'loading' | 'unlinked' | 'error' | 'ready';

interface OrgPublishDestinationsState {
  destinations: OrgPublishDestination[];
  status: OrgLookupStatus;
}

interface FigmaOrgMembership {
  /** Users in Figma orgs are not allowed to publish to Community. */
  isBlockedByFigmaOrg: boolean;
  isLoading: boolean;
}

const EMPTY_DESTINATIONS: OrgPublishDestination[] = [];

export const ELIGIBLE_FIGMA_ORGS_QUERY_KEY = 'figmaPublishingEligibleOrgs';

/**
 * Figma orgs a tool can be published to, in the order the server returns them. An org whose admin
 * turned publishing off is never offered, and neither is any org when an Enterprise Weave workspace's
 * admin hasn't turned org publishing on.
 */
export const useOrgPublishDestinations = (enabled = true): OrgPublishDestinationsState => {
  const { value: isOrgPublishingEnabled } = useFeatureFlag(FF_FIGMA_ORG_SCOPED_PUBLISHING);
  const isWorkspaceOrgPublishingAllowed = useWorkspacesStore(
    ({ activeWorkspace }) =>
      !isEnterpriseWeaveSubscription(activeWorkspace.subscription?.type) ||
      activeWorkspace.preferences?.isFigmaOrgPublishingEnabled === true,
  );
  const isFeatureEnabled = enabled && isOrgPublishingEnabled === true && isWorkspaceOrgPublishingAllowed;
  const { figmaUserId, isLoadingConnection, data, isLoading, error } = useEligibleFigmaOrgs(
    isFeatureEnabled,
    selectPublishDestinations,
  );

  if (!isFeatureEnabled) {
    return { destinations: EMPTY_DESTINATIONS, status: 'ready' };
  }
  if (isLoadingConnection) {
    return { destinations: EMPTY_DESTINATIONS, status: 'loading' };
  }
  if (figmaUserId === null || error instanceof NotConnectedToFigmaError) {
    return { destinations: EMPTY_DESTINATIONS, status: 'unlinked' };
  }
  // A failed background refetch keeps the last good list, so an open org publish never drops to Community.
  if (data) {
    return { destinations: data, status: 'ready' };
  }
  if (isLoading) {
    return { destinations: EMPTY_DESTINATIONS, status: 'loading' };
  }
  return { destinations: EMPTY_DESTINATIONS, status: 'error' };
};

/**
 * Whether the linked Figma account sits in any Figma org, which rules out Community publishing. Shares the
 * eligible-orgs request with {@link useOrgPublishDestinations}, but counts orgs whose admin turned
 * publishing off too. Not linked, flag off or unresolved, and a failed lookup all read as not blocked.
 */
export const useFigmaOrgMembership = (enabled = true): FigmaOrgMembership => {
  const { value: isOrgPublishingEnabled } = useFeatureFlag(FF_FIGMA_ORG_SCOPED_PUBLISHING);
  const isFeatureEnabled = enabled && isOrgPublishingEnabled === true;
  const { isLoadingConnection, data, isLoading } = useEligibleFigmaOrgs(isFeatureEnabled, hasAnyFigmaOrg);

  // A disabled query still hands back its cached orgs, so turning the flag off has to win over them.
  if (!isFeatureEnabled) {
    return { isBlockedByFigmaOrg: false, isLoading: false };
  }
  if (isLoadingConnection || isLoading) {
    return { isBlockedByFigmaOrg: false, isLoading: true };
  }
  return { isBlockedByFigmaOrg: data === true, isLoading: false };
};

const useEligibleFigmaOrgs = <T>(enabled: boolean, select: (orgs: EligibleFigmaOrg[]) => T) => {
  const { data: connection, isLoading: isLoadingConnection } = useLinkedFigmaAccount();
  const figmaUserId = connection?.connected ? connection.figmaUserId : null;
  const query = useQuery({
    // Keyed by the Figma account so linking a different one never shows the previous account's orgs.
    queryKey: [ELIGIBLE_FIGMA_ORGS_QUERY_KEY, figmaUserId],
    queryFn: fetchEligibleFigmaOrgs,
    enabled: enabled && figmaUserId !== null,
    retry: (failureCount, retryError) => !(retryError instanceof NotConnectedToFigmaError) && failureCount < 1,
    select,
  });
  return { ...query, figmaUserId, isLoadingConnection };
};

const selectPublishDestinations = (orgs: EligibleFigmaOrg[]): OrgPublishDestination[] =>
  orgs.filter((org) => org.publishing_enabled).map((org) => ({ planId: org.plan_id, name: org.name }));

const hasAnyFigmaOrg = (orgs: EligibleFigmaOrg[]): boolean => orgs.length > 0;
